import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

type Props = {
  id: string;
  num: string;
  eyebrow: string;
  sectionLabel: string;
  title: ReactNode;
  description: string;
  bullets: ReadonlyArray<string>;
  demo: ReactNode;
  Icon: LucideIcon;
  reversed?: boolean;
  tone?: 'paper' | 'white';
  /** Lien optionnel « En savoir plus » (ancre interne descriptive). */
  cta?: { label: string; to: string };
};

export default function FeatureModuleSection({
  id,
  num,
  eyebrow,
  sectionLabel,
  title,
  description,
  bullets,
  demo,
  Icon,
  reversed = false,
  tone = 'paper',
  cta,
}: Props) {
  return (
    <section
      id={id}
      className={clsx(
        'relative scroll-mt-24 py-20 md:py-28',
        tone === 'paper' ? 'bg-paper' : 'bg-white',
      )}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div
          className={clsx(
            'grid items-center gap-12 md:gap-16',
            reversed ? 'md:grid-cols-[1fr_1fr]' : 'md:grid-cols-[1fr_1fr]',
          )}
        >
          <div className={clsx(reversed && 'md:order-last')}>
            <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.18em] text-charcoal-400">
              {num}
            </div>
            <SectionLabel className="mt-2">{eyebrow}</SectionLabel>

            <div className="mt-5 inline-flex h-14 w-14 items-center justify-center border-2 border-charcoal-800 bg-white shadow-neobrutal-4">
              <Icon className="h-6 w-6 text-charcoal-800" strokeWidth={2.2} />
            </div>

            <h3 className="mt-6 font-marketing-display text-[clamp(30px,3.8vw,52px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
              {title}
            </h3>

            <p className="mt-5 max-w-xl font-marketing-body text-[17px] leading-[1.55] text-charcoal-600 md:text-[18px]">
              {description}
            </p>

            <div className="mt-6 border-t-2 border-dashed border-charcoal-200 pt-5">
              <div className="mb-3 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                {sectionLabel}
              </div>
              <ul className="space-y-3">
                {bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 font-marketing-body text-[15px] text-charcoal-700 md:text-[15.5px]"
                  >
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.8"
                      >
                        <path d="M2.5 6l2.5 2.5 5-5" />
                      </svg>
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {cta && (
              <div className="mt-7">
                <NeobrutalButton as="link" to={cta.to} variant="primary" size="md">
                  {cta.label}
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14" />
                    <path d="M13 6l6 6-6 6" />
                  </svg>
                </NeobrutalButton>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center">{demo}</div>
        </div>
      </div>
    </section>
  );
}
